'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import { toasterApi } from '../../toast/ToastProvider';
import type { ChatCheckpointCallbacks } from '../components/chat-message-list-editing';

export type RestoreChatCheckpoint = (args: {
  threadId: string;
  messageId: string;
}) => Promise<{ restoredFileCount: number; canRedo: boolean }>;

export type RedoChatCheckpointRestore = (args: {
  threadId: string;
}) => Promise<{ restoredFileCount: number }>;

export type UseChatCheckpointRestoreParams = {
  threadId: string;
  restoreCheckpoint?: RestoreChatCheckpoint;
  redoRestore?: RedoChatCheckpointRestore;
};

export function useChatCheckpointRestore({
  threadId,
  restoreCheckpoint,
  redoRestore,
}: UseChatCheckpointRestoreParams): ChatCheckpointCallbacks | undefined {
  const t = useTranslations('designSystem.chat.userMessageCard');
  const [restoredMessageId, setRestoredMessageId] = useState<string>();
  const [isPending, setIsPending] = useState(false);

  useEffect(() => {
    setRestoredMessageId(undefined);
  }, [threadId]);

  const onRestoreCheckpoint = useCallback(
    async (messageId: string) => {
      if (!restoreCheckpoint || isPending) {
        return;
      }

      setIsPending(true);

      try {
        const result = await restoreCheckpoint({ threadId, messageId });

        setRestoredMessageId(result.canRedo ? messageId : undefined);
        toasterApi.success({
          title: t('restoreCheckpointSucceededTitle'),
          description: t('restoreCheckpointSucceededDescription', {
            count: result.restoredFileCount,
          }),
        });
      } catch (error: unknown) {
        console.warn('[useChatCheckpointRestore] restore failed', error);
        toasterApi.error({
          title: t('restoreCheckpointFailedTitle'),
          description: t('restoreCheckpointFailedDescription'),
        });
      } finally {
        setIsPending(false);
      }
    },
    [isPending, restoreCheckpoint, t, threadId],
  );

  const onRedoRestore = useCallback(async () => {
    if (!redoRestore || isPending) {
      return;
    }

    setIsPending(true);

    try {
      await redoRestore({ threadId });
      setRestoredMessageId(undefined);
      toasterApi.success({ title: t('redoRestoreSucceededTitle') });
    } catch (error: unknown) {
      console.warn('[useChatCheckpointRestore] redo failed', error);
      toasterApi.error({
        title: t('redoRestoreFailedTitle'),
        description: t('redoRestoreFailedDescription'),
      });
    } finally {
      setIsPending(false);
    }
  }, [isPending, redoRestore, t, threadId]);

  return useMemo(() => {
    if (!restoreCheckpoint || !redoRestore) {
      return undefined;
    }

    return {
      onRestoreCheckpoint: (messageId: string) => {
        void onRestoreCheckpoint(messageId);
      },
      onRedoRestore: () => {
        void onRedoRestore();
      },
      restoredMessageId,
      isPending,
    };
  }, [
    isPending,
    onRedoRestore,
    onRestoreCheckpoint,
    redoRestore,
    restoreCheckpoint,
    restoredMessageId,
  ]);
}
